import { FormEvent, useEffect, useMemo, useState } from 'react'
import { Plus, Users, Pencil, Check, X, Upload, XCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { createClass, createClassStudent, fetchClasses, fetchClassStudents, updateStudent } from '../api/client'
import type { ClassSummary, StudentSummary } from '../api/types'
import { useOrgContext } from '../state/OrgContext'

export default function ClassesPage() {
  const { t } = useTranslation('pages')
  const [classes, setClasses] = useState<ClassSummary[]>([])
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null)
  const [students, setStudents] = useState<StudentSummary[]>([])
  const { activeRole } = useOrgContext()

  const [loadingClasses, setLoadingClasses] = useState(true)
  const [loadingStudents, setLoadingStudents] = useState(false)
  const [savingClass, setSavingClass] = useState(false)
  const [savingStudent, setSavingStudent] = useState(false)
  const [savingStudentEdit, setSavingStudentEdit] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Bulk import modal state
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [bulkListText, setBulkListText] = useState('')
  const [savingBulk, setSavingBulk] = useState(false)

  const [newClassName, setNewClassName] = useState('')
  const [newStudentName, setNewStudentName] = useState('')
  const [newStudentNo, setNewStudentNo] = useState('')
  const [editingStudentId, setEditingStudentId] = useState<number | null>(null)
  const [editingStudentName, setEditingStudentName] = useState('')
  const [editingStudentNo, setEditingStudentNo] = useState('')

  const canManage = activeRole === 'ORG_MANAGER' || activeRole === 'ADMIN'

  const selectedClass = useMemo(
    () => classes.find((item) => item.id === selectedClassId) || null,
    [classes, selectedClassId],
  )

  const loadBaseData = async () => {
    setLoadingClasses(true)
    setError(null)
    try {
      const classData = await fetchClasses()
      setClasses(classData)
      if (classData.length > 0) {
        setSelectedClassId((prev) => prev ?? classData[0].id)
      } else {
        setSelectedClassId(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('classes.loadError'))
    } finally {
      setLoadingClasses(false)
    }
  }

  const loadStudents = async (classId: number) => {
    setLoadingStudents(true)
    setError(null)
    try {
      const list = await fetchClassStudents(classId)
      setStudents(list)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('classes.studentsLoadError'))
      setStudents([])
    } finally {
      setLoadingStudents(false)
    }
  }

  useEffect(() => {
    loadBaseData()
  }, [])

  useEffect(() => {
    if (!selectedClassId) {
      setStudents([])
      return
    }
    loadStudents(selectedClassId)
  }, [selectedClassId])

  const onCreateClass = async (event: FormEvent) => {
    event.preventDefault()
    const trimmed = newClassName.trim()
    if (!trimmed) {
      setError(t('classes.classNameRequired'))
      return
    }

    setSavingClass(true)
    setError(null)
    try {
      const created = await createClass(trimmed)
      setClasses((prev) => [created, ...prev])
      setSelectedClassId(created.id)
      setNewClassName('')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('classes.createError'))
    } finally {
      setSavingClass(false)
    }
  }

  const onAddStudent = async (event: FormEvent) => {
    event.preventDefault()
    if (!selectedClassId) {
      setError(t('classes.selectClassFirst'))
      return
    }

    const fullName = newStudentName.trim()
    const studentNo = newStudentNo.trim()
    if (!fullName || !studentNo) {
      setError(t('classes.studentNameNumberRequired'))
      return
    }

    setSavingStudent(true)
    setError(null)
    try {
      await createClassStudent(selectedClassId, { full_name: fullName, student_no: studentNo })
      setNewStudentName('')
      setNewStudentNo('')
      await loadStudents(selectedClassId)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('classes.addStudentError'))
    } finally {
      setSavingStudent(false)
    }
  }

  const onBulkImport = async () => {
    if (!selectedClassId) {
      setError(t('classes.selectClassFirst'))
      return
    }

    const lines = bulkListText.trim().split('\n').filter(line => line.trim())
    if (lines.length === 0) {
      setError(t('classes.listEmpty'))
      return
    }

    setSavingBulk(true)
    setError(null)
    try {
      let successCount = 0
      let failCount = 0

      for (const line of lines) {
        const parts = line.trim().split(/\s+/)
        if (parts.length < 2) {
          failCount++
          continue
        }
        // Last part is student number, rest is name
        const studentNo = parts[parts.length - 1]
        const fullName = parts.slice(0, -1).join(' ')
        
        if (!fullName || !studentNo) {
          failCount++
          continue
        }

        try {
          await createClassStudent(selectedClassId, { full_name: fullName, student_no: studentNo })
          successCount++
        } catch {
          failCount++
        }
      }

      if (successCount > 0) {
        await loadStudents(selectedClassId)
      }

      setShowBulkModal(false)
      setBulkListText('')
      if (failCount > 0) {
        setError(t('classes.bulkAddResult', { added: successCount, failed: failCount }))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('classes.bulkAddError'))
    } finally {
      setSavingBulk(false)
    }
  }

  const startStudentEdit = (student: StudentSummary) => {
    setEditingStudentId(student.id)
    setEditingStudentName(student.full_name)
    setEditingStudentNo(student.student_no)
    setError(null)
  }

  const cancelStudentEdit = () => {
    setEditingStudentId(null)
    setEditingStudentName('')
    setEditingStudentNo('')
  }

  const saveStudentEdit = async () => {
    if (!editingStudentId || !selectedClassId) return

    const fullName = editingStudentName.trim()
    const studentNo = editingStudentNo.trim()
    if (!fullName || !studentNo) {
      setError(t('classes.studentNameNumberRequired'))
      return
    }

    setSavingStudentEdit(true)
    setError(null)
    try {
      await updateStudent(editingStudentId, { full_name: fullName, student_no: studentNo })
      await loadStudents(selectedClassId)
      cancelStudentEdit()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('classes.updateStudentError'))
    } finally {
      setSavingStudentEdit(false)
    }
  }

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h2 className="page-header-title">{t('classes.title')}</h2>
        <p className="page-header-sub">{t('classes.subtitle')}</p>
      </div>

      {error && (
        <div className="card card-padding mb-20" style={{ borderLeft: '3px solid var(--color-danger)' }}>
          <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>
        </div>
      )}

      <div className="two-col-layout">
        {/* Left column — class list + create */}
        <div>
          {canManage && (
            <div className="card card-padding mb-20">
              <h3 className="card-title mb-16">{t('classes.createCard')}</h3>
              <form className="flex gap-12" onSubmit={onCreateClass}>
                <div className="form-group" style={{ flex: 1 }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t('classes.classNamePlaceholder')}
                    value={newClassName}
                    onChange={(e) => setNewClassName(e.target.value)}
                  />
                </div>
                <button className="btn btn-primary" type="submit" disabled={savingClass}>
                  <Plus size={16} />
                  {savingClass ? t('common.creating') : t('common.create')}
                </button>
              </form>
            </div>
          )}

          <div className="card">
            {loadingClasses ? (
              <div className="card-padding">
                <p className="text-sm text-muted">{t('classes.classesLoading')}</p>
              </div>
            ) : classes.length === 0 ? (
              <div className="card-padding">
                <p className="text-sm text-muted">{t('classes.classesEmpty')}</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('classes.classCol')}</th>
                      <th></th>
                    </tr>
                  </thead>
                   <tbody>
                     {classes.map((c) => {
                       const isSelected = selectedClassId === c.id
                       return (
                         <tr
                           key={c.id}
                           onClick={() => setSelectedClassId(c.id)}
                           style={{ cursor: 'pointer' }}
                           className={isSelected ? 'table-row-selected' : ''}
                         >
                           <td className="font-semibold">{c.name}</td>
                           <td></td>
                         </tr>
                       )
                     })}
                   </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right column — students of selected class */}
        <div>
          <div className="card card-padding">
            <div className="flex items-center justify-between mb-16">
              <div>
                <h3 className="card-title">{selectedClass ? selectedClass.name : t('classes.studentsHeading')}</h3>
                <p className="text-sm text-muted mt-4">
                  {selectedClass
                    ? t('classes.nStudents', { count: students.length })
                    : t('classes.selectClassHint')}
                </p>
              </div>
              {selectedClass && (
                <span className="badge badge-copper">
                  <Users size={12} />
                  {students.length}
                </span>
              )}
            </div>

            {selectedClass && canManage && (
              <form className="mb-20" onSubmit={onAddStudent}>
                <div className="flex gap-12">
                  <div className="form-group" style={{ flex: 1 }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={t('classes.studentNamePlaceholder')}
                      value={newStudentName}
                      onChange={(e) => setNewStudentName(e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ width: 140 }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={t('classes.studentNumberPlaceholder')}
                      value={newStudentNo}
                      onChange={(e) => setNewStudentNo(e.target.value)}
                    />
                  </div>
                  <button className="btn btn-primary" type="submit" disabled={savingStudent}>
                    <Plus size={16} />
                    {savingStudent ? t('common.adding') : t('common.add')}
                  </button>
                  <button
                    className="btn btn-outline"
                    type="button"
                    onClick={() => setShowBulkModal(true)}
                  >
                    <Upload size={16} />
                    {t('classes.bulkUpload')}
                  </button>
                </div>
              </form>
            )}

            {selectedClass && (
              <div className="table-wrapper" style={{ maxHeight: 460, overflowY: 'auto' }}>
                {loadingStudents ? (
                  <p className="text-sm text-muted" style={{ padding: 12 }}>{t('classes.studentsLoading2')}</p>
                ) : students.length === 0 ? (
                  <p className="text-sm text-muted" style={{ padding: 12 }}>{t('classes.studentsEmpty')}</p>
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>{t('classes.colHash')}</th>
                        <th>{t('classes.colFullName')}</th>
                        <th>{t('classes.colStudentNo')}</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s, i) => {
                        const isEditing = editingStudentId === s.id
                        return (
                          <tr key={s.id}>
                            <td className="text-muted text-xs">{i + 1}</td>
                            <td>
                              {isEditing ? (
                                <input
                                  className="form-input"
                                  value={editingStudentName}
                                  onChange={(e) => setEditingStudentName(e.target.value)}
                                  disabled={savingStudentEdit}
                                  style={{ padding: '4px 8px', fontSize: '0.85rem' }}
                                />
                              ) : (
                                <span className="font-semibold">{s.full_name}</span>
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <input
                                  className="form-input"
                                  value={editingStudentNo}
                                  onChange={(e) => setEditingStudentNo(e.target.value)}
                                  disabled={savingStudentEdit}
                                  style={{ padding: '4px 8px', fontSize: '0.85rem' }}
                                />
                              ) : (
                                <span className="text-muted">{s.student_no}</span>
                              )}
                            </td>
                            <td>
                              {canManage && (
                                <>
                                  {!isEditing ? (
                                    <button
                                      className="btn btn-icon btn-ghost"
                                      onClick={() => startStudentEdit(s)}
                                      title={t('classes.editTitle')}
                                    >
                                      <Pencil size={14} style={{ color: 'var(--text-muted)' }} />
                                    </button>
                                  ) : (
                                    <div className="flex gap-4">
                                      <button
                                        className="btn btn-icon btn-ghost"
                                        onClick={saveStudentEdit}
                                        disabled={savingStudentEdit}
                                        title={t('classes.saveTitle')}
                                      >
                                        <Check size={14} style={{ color: 'var(--color-success)' }} />
                                      </button>
                                      <button
                                        className="btn btn-icon btn-ghost"
                                        onClick={cancelStudentEdit}
                                        disabled={savingStudentEdit}
                                        title={t('classes.cancelTitle')}
                                      >
                                        <X size={14} style={{ color: 'var(--color-danger)' }} />
                                      </button>
                                    </div>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{t('classes.bulkModalTitle')}</h3>
              <button className="btn btn-icon btn-ghost" onClick={() => setShowBulkModal(false)}>
                <XCircle size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="text-sm text-muted mb-16">
                {t('classes.bulkModalDesc')}
              </p>
              <div className="bulk-import-header">
                <span>{t('classes.bulkColName')}</span>
                <span>{t('classes.bulkColNumber')}</span>
              </div>
              <textarea
                className="form-input"
                style={{ height: 240, fontFamily: 'monospace', resize: 'vertical' }}
                placeholder={"Ahmet Yilmaz 123\nMehmet Demir 456\nAyse Kaya 789"}
                value={bulkListText}
                onChange={(e) => setBulkListText(e.target.value)}
              />
              {error && (
                <p className="text-sm text-danger mt-8">{error}</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowBulkModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={onBulkImport}
                disabled={savingBulk || !bulkListText.trim()}
              >
                {savingBulk ? t('common.adding') : t('common.add')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
